// v2.4 — name-similarity helpers used only by ClientAddDialog's
// advisory "similar clients already exist" hint. The hint never
// blocks a submit; the helpers are intentionally cheap and
// approximate. Reverse-ported from the design prototype's
// `data.jsx` and kept purely client-side: the server's dup-check is
// case-insensitive exact-match only.
//
// Three functions:
//   normalizeClientName(name)      → canonical comparable form
//   clientSimilarity(a, b)         → [0, 1] similarity score
//   findSimilarClients(name, list) → ranked matches above threshold

// Common legal / company-form suffixes we strip before comparing so
// "Acme Inc." and "Acme Corporation" collapse to the same token set.
const COMPANY_SUFFIX_RE =
  /\b(inc|incorporated|llc|ltd|limited|corp|corporation|co|company|gmbh|ag|sa|spa|pty|plc|holdings|group|sdn bhd|bhd|kk)\b\.?/g;

/**
 * Normalize a client name for comparison. Lower-cases, decomposes
 * accents (NFKD + combining-mark strip), drops company suffixes,
 * replaces non-alphanumerics with spaces, and collapses whitespace.
 * Stable + idempotent.
 */
export function normalizeClientName(name) {
  if (!name) return '';
  return String(name)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(COMPANY_SUFFIX_RE, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Token Jaccard — symmetric, robust to word order. Empty inputs → 0.
function tokenJaccard(a, b) {
  if (!a || !b) return 0;
  const tokensA = new Set(a.split(' ').filter(Boolean));
  const tokensB = new Set(b.split(' ').filter(Boolean));
  if (tokensA.size === 0 || tokensB.size === 0) return 0;
  let intersection = 0;
  for (const t of tokensA) if (tokensB.has(t)) intersection += 1;
  const union = tokensA.size + tokensB.size - intersection;
  return intersection / union;
}

// Levenshtein distance, O(|a| * |b|) time + O(min(|a|, |b|)) memory.
// Used to catch typo-style matches Jaccard misses ("Robonsons" vs
// "Robinsons"). We never feed it pathologically long strings; client
// names are at most ~120 chars per the server validator.
function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  // Ensure a is the shorter — keeps the working row small.
  if (a.length > b.length) {
    const tmp = a;
    a = b;
    b = tmp;
  }
  let prev = new Array(a.length + 1);
  for (let i = 0; i <= a.length; i += 1) prev[i] = i;
  let curr = new Array(a.length + 1);
  for (let j = 1; j <= b.length; j += 1) {
    curr[0] = j;
    for (let i = 1; i <= a.length; i += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[i] = Math.min(
        curr[i - 1] + 1,
        prev[i] + 1,
        prev[i - 1] + cost,
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[a.length];
}

function levRatio(a, b) {
  if (!a && !b) return 1;
  const maxLen = Math.max(a.length, b.length);
  if (!maxLen) return 0;
  return 1 - levenshtein(a, b) / maxLen;
}

/**
 * Similarity score between two raw client names, in [0, 1]. Combines
 * token Jaccard (multi-word, order-insensitive) with a Levenshtein
 * ratio (typo-tolerant) and takes the max — either signal alone is
 * enough to flag a likely-duplicate. An exact-after-normalize match
 * scores 1.
 */
export function clientSimilarity(a, b) {
  const normA = normalizeClientName(a);
  const normB = normalizeClientName(b);
  if (!normA || !normB) return 0;
  if (normA === normB) return 1;
  return Math.max(tokenJaccard(normA, normB), levRatio(normA, normB));
}

/**
 * Find names in `existing` similar to `name`, ranked descending. The
 * exact-match (case-insensitive) is excluded — the caller flags that
 * separately as a hard duplicate, so the hint should only surface
 * *near-misses*. Default threshold 0.6 favours recall over precision;
 * a false positive is fine since the hint is purely advisory.
 */
export function findSimilarClients(name, existing, { threshold = 0.6 } = {}) {
  const target = String(name || '').trim();
  if (!target) return [];
  const targetLower = target.toLowerCase();
  const scored = [];
  for (const entry of existing || []) {
    const otherName = typeof entry === 'string' ? entry : entry?.name || '';
    if (!otherName) continue;
    if (otherName.toLowerCase() === targetLower) continue;
    const score = clientSimilarity(target, otherName);
    if (score >= threshold) scored.push({ name: otherName, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored;
}
